import axios from "axios";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({ baseURL: API_URL });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("usg_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      const path = window.location.pathname;
      if (!path.startsWith("/invite/")) {
        localStorage.removeItem("usg_token");
        localStorage.removeItem("usg_user");
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

export const authAPI = {
  register: (data) => api.post("/auth/register", data),
  login: (data) => api.post("/auth/login", data),
  me: () => api.get("/auth/me"),
  markOnboarded: () => api.put("/auth/onboarded"),
  setPassword: (password) => api.put("/auth/set-password", { password }),
};

export const boardAPI = {
  create: (data) => api.post("/boards", data),
  list: () => api.get("/boards"),
  get: (id) => api.get(`/boards/${id}`),
  delete: (id) => api.delete(`/boards/${id}`),
  lock: (id, isLocked) => api.put(`/boards/${id}/lock`, { is_locked: isLocked }),
  updateStatus: (id) => api.put(`/boards/${id}/status`),
  toggleStatus: (id, status) => api.put(`/boards/${id}/status/toggle`, { status }),
  updateSetupStep: (id, step) => api.put(`/boards/${id}/setup-step`, { step }),
  updateSettings: (id, data) => api.put(`/boards/${id}/settings`, data),
  analytics: (id) => api.get(`/boards/${id}/analytics`),
  exportBoard: (id, format = "json") => api.get(`/boards/${id}/export?format=${format}`),
  addCustomStall: (boardId, deptId, data) => api.post(`/boards/${boardId}/departments/${deptId}/stalls`, data),
  addDepartment: (boardId, data) => api.post(`/boards/${boardId}/departments`, data),
};

export const multiplaiAPI = {
  getStatus: () => api.get("/multiplai/status"),
  getOrganizations: () => api.get("/multiplai/organizations"),
  getDepartments: (orgId) => api.get(`/multiplai/departments${orgId ? `?org_id=${orgId}` : ""}`),
  getUsers: (orgId) => api.get(`/multiplai/users${orgId ? `?org_id=${orgId}` : ""}`),
  authorize: () => api.get("/auth/multiplai/authorize"),
  callback: (code, state) => api.post(`/auth/multiplai/callback?code=${code}&state=${state || ""}`),
  confirmLink: (pendingLinkId) => api.post(`/auth/multiplai/confirm-link?pending_link_id=${pendingLinkId}`),
  declineLink: (pendingLinkId) => api.post(`/auth/multiplai/decline-link?pending_link_id=${pendingLinkId}`),
  connect: (code) => api.post(`/auth/multiplai/connect?code=${code}`),
  disconnect: () => api.post("/auth/multiplai/disconnect"),
  setOrg: (orgId) => api.put(`/auth/multiplai/org?org_id=${orgId}`),
};

export const stallAPI = {
  updateDecision: (boardId, stallId, data) =>
    api.put(`/boards/${boardId}/stalls/${stallId}/decision`, data),
};

export const deptAPI = {
  toggle: (boardId, deptId, isApplicable) =>
    api.put(`/boards/${boardId}/departments/${deptId}/toggle`, { is_applicable: isApplicable }),
};

export const inviteAPI = {
  send: (boardId, data) => api.post(`/boards/${boardId}/invite`, data),
  getParticipants: (boardId) => api.get(`/boards/${boardId}/participants`),
  removeParticipant: (boardId, userId) => api.delete(`/boards/${boardId}/participants/${userId}`),
  accept: (token) => axios.get(`${API_URL}/invite/${token}`),
};

export const videoAPI = {
  getProgress: (boardId) => api.get(`/boards/${boardId}/videos`),
  markWatched: (boardId, videoIndex) => api.put(`/boards/${boardId}/videos/watched`, { video_index: videoIndex }),
  dismiss: (boardId) => api.put(`/boards/${boardId}/videos/dismiss`),
};

export const aiAPI = {
  suggest: (data) => api.post("/ai/suggest", data),
};

export const voteAPI = {
  cast: (boardId, stallId, data) => api.post(`/boards/${boardId}/stalls/${stallId}/vote`, data),
  getMyVotes: (boardId) => api.get(`/boards/${boardId}/votes/my`),
  getCounts: (boardId) => api.get(`/boards/${boardId}/votes/counts`),
  reveal: (boardId) => api.post(`/boards/${boardId}/votes/reveal`),
  getResults: (boardId) => api.get(`/boards/${boardId}/votes/results`),
  override: (boardId, stallId, data) => api.put(`/boards/${boardId}/stalls/${stallId}/override`, data),
  updateSettings: (boardId, data) => api.put(`/boards/${boardId}/voting-settings`, data),
  assignDepartments: (boardId, data) => api.put(`/boards/${boardId}/assign-departments`, data),
};

export const disruptionAPI = {
  createSession: (boardId) => api.post("/disruption", { board_id: boardId }),
  getSession: (boardId) => api.get(`/disruption/${boardId}`),
  updateSettings: (boardId, data) => api.put(`/disruption/${boardId}/settings`, data),
  advancePhase: (boardId, phase) => api.put(`/disruption/${boardId}/phase`, { phase }),
  assignPairs: (boardId, data) => api.post(`/disruption/${boardId}/pairs`, data),
  assignGroups: (boardId, data) => api.post(`/disruption/${boardId}/groups`, data),
  updateGroups: (boardId, groups) => api.put(`/disruption/${boardId}/groups`, { groups }),
  advanceBrainstormPhase: (boardId, subPhase) => api.put(`/disruption/${boardId}/brainstorm-phase`, { sub_phase: subPhase }),
  submitResponse: (boardId, data) => api.post(`/disruption/${boardId}/responses`, data),
  getResponses: (boardId) => api.get(`/disruption/${boardId}/responses`),
  getMyResponses: (boardId) => api.get(`/disruption/${boardId}/responses/my`),
  submitIdea: (boardId, data) => api.post(`/disruption/${boardId}/ideas`, data),
  getIdeas: (boardId, groupId) => api.get(`/disruption/${boardId}/ideas${groupId ? `?group_id=${groupId}` : ""}`),
  updateIdea: (boardId, ideaId, data) => api.put(`/disruption/${boardId}/ideas/${ideaId}`, data),
  deleteIdea: (boardId, ideaId) => api.delete(`/disruption/${boardId}/ideas/${ideaId}`),
  addNote: (boardId, ideaId, text) => api.post(`/disruption/${boardId}/ideas/${ideaId}/notes`, { text }),
  savePositions: (boardId, positions) => api.put(`/disruption/${boardId}/ideas/positions`, { positions }),
  clusterIdeas: (boardId) => api.post(`/disruption/${boardId}/ideas/cluster`),
  mergeIdeas: (boardId, ideaIds) => api.post(`/disruption/${boardId}/ideas/merge`, { idea_ids: ideaIds }),
  unmergeIdea: (boardId, ideaId) => api.post(`/disruption/${boardId}/ideas/${ideaId}/unmerge`),
  castVotes: (boardId, ideaIds) => api.post(`/disruption/${boardId}/votes`, { idea_ids: ideaIds }),
  getVoteResults: (boardId) => api.get(`/disruption/${boardId}/votes/results`),
  exportSession: (boardId) => api.get(`/disruption/${boardId}/export`),
  downloadPDF: (boardId) => api.get(`/disruption/${boardId}/pdf`, { responseType: "blob" }),
};

export default api;
